import { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import useApi from '../../Api';

export const useOriginSearch = (navigate, onSelectSuggestion = null) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const apiClient = useApi();
  const cache = useRef({});

  const fetchSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length < 1) {
        setSuggestions([]);
        setIsLoading(false);
        return;
      }
      if (cache.current[query]) {
        setSuggestions(cache.current[query]);
        setIsLoading(false);
        setError(null);
        return;
      }
      try {
        const response = await apiClient.get(`/autocomplete?q=${encodeURIComponent(query)}`);
        if (response.status === 200) {
          const data = response.data;
          cache.current[query] = data;
          setSuggestions(data);
          setError(null);
        } else {
          setSuggestions([]);
          setError('Error fetching suggestions');
        }
      } catch (err) {
        setSuggestions([]);
        setError('Error fetching suggestions');
      }
      setIsLoading(false);
    }, 300),
    [apiClient]
  );

  useEffect(() => {
    if (searchQuery) {
      setIsLoading(true);
      fetchSuggestions(searchQuery);
    } else {
      setSuggestions([]);
      setIsLoading(false);
      setError(null);
    }
  }, [searchQuery, fetchSuggestions]);

  const debouncedHandleFocus = useCallback(
    debounce(() => {
      if (searchQuery && suggestions.length === 0) {
        fetchSuggestions(searchQuery);
      }
    }, 100),
    [searchQuery, suggestions, fetchSuggestions]
  );

  const handleFocus = () => {
    debouncedHandleFocus();
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const debouncedHandleSuggestionClick = useCallback(
    debounce((suggestion) => {
      setSearchQuery(suggestion.name);
      setSuggestions([]);
      setError(null);
      if (onSelectSuggestion && typeof onSelectSuggestion === 'function') {
        onSelectSuggestion(suggestion);
      } else {
        navigate(`/destination/${encodeURIComponent(suggestion.origin_id)}`);
      }
    }, 100),
    [navigate, onSelectSuggestion]
  );

  const handleSuggestionClick = (suggestion) => {
    debouncedHandleSuggestionClick(suggestion);
  };

  const handleClickOutside = useCallback(
    (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setSuggestions([]);
        setError(null);
      }
    },
    [searchInputRef, dropdownRef]
  );

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) {
        const topSuggestion = suggestions[0];
        handleSuggestionClick(topSuggestion);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
    }
  };

  return {
    searchQuery,
    suggestions,
    isLoading,
    error,
    searchInputRef,
    dropdownRef,
    handleSearchChange,
    handleSuggestionClick,
    handleClickOutside,
    handleKeyPress,
    handleFocus,
  };
};
